import { useState, useEffect } from 'react';
import { fetchGroundStationList } from 'api/groundStation';
import { DashboardContactSchedule } from '../../utils/DashboardContactSchedule';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import axios from 'common/AxiosAuth';
import { getScheduleBuffer } from 'api/scheduleBuffer';

dayjs.extend(utc);
dayjs.extend(isBetween);

export const useSatelliteSchedule = () => {
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bufferMinutes, setBufferMinutes] = useState<number>(5); // 기본값 5분

  // 버퍼 설정 가져오기
  useEffect(() => {
    const fetchBuffer = async () => {
      try {
        const data = await getScheduleBuffer();
        setBufferMinutes(data?.bufferMinutes ?? 5); // 설정된 버퍼 사용, 기본값 5분
      } catch (e) {
        console.error('Failed to fetch schedule buffer:', e);
        setBufferMinutes(5); // 오류 시 5분으로 폴백
      }
    };
    fetchBuffer();
  }, []);

  const fetchSatelliteSchedule = async (satellite: any) => {
    setIsLoading(true);
    setScheduleData([]);
    
    try {
      // 지상국 정보 가져오기
      const registeredStations = await fetchGroundStationList();
      
      if (!registeredStations || registeredStations.length === 0) {
        console.log('등록된 지상국이 없습니다.');
        setIsLoading(false);
        return;
      }
      
      // Schedule a Contact와 완전히 동일한 날짜 범위 (UTC 기준 당일)
      const todayStrUTC = dayjs.utc().format('YYYY-MM-DD'); // 오늘 날짜 (UTC)
      const startDate = dayjs.utc(todayStrUTC); // 오늘 00:00:00 (UTC)
      const endDate = dayjs.utc(todayStrUTC).add(1, 'day'); // 내일 00:00:00 (UTC)
            
      // Schedule a Contact에서 사용하는 "Sacheon" 지상국 찾기
      const sacheonStation = registeredStations.find((station: any) => 
        station.name === 'Sacheon' || 
        station.name === 'SACHEON A/T' || 
        station.name?.includes('Sacheon') ||
        station.name?.includes('사천')
      );
      
      const selectedStation = sacheonStation || registeredStations[0]; // Sacheon이 없으면 첫 번째
      
      // 선택된 위성의 스케줄 계산
      const allSchedules: any[] = [];
      
      try {
        const tle = `${satellite.spacecraft.tleFirst}\n${satellite.spacecraft.tleSecond}`;
        const contactSchedule = new DashboardContactSchedule(tle);
        
        const groundStationPosition = {
          longitude: (selectedStation as any).longitudeDegrees,
          latitude: (selectedStation as any).latitudeDegrees,
          height: (selectedStation as any).altitudeMeters,
        };
        
        contactSchedule.updatePasses(
          satellite.spacecraft.tleTitle,
          groundStationPosition,
          startDate,
          endDate
        );
        
        // Schedule a Contact와 동일한 필터링 및 변환
        const minContactDuration = (selectedStation as any).minContactDuration || 0;
        const minElevationDegrees = (selectedStation as any).minElevationDegrees || 0;
        
        const spacecraftSchedules = contactSchedule.passes
          .filter((pass: any) => pass.AvailableTime > minContactDuration)
          .filter((pass: any) => pass.MaxElevation >= minElevationDegrees)
          .map((pass: any) => {
            const passStartUTC = dayjs.utc(pass.StartTime);
            const passEndUTC = dayjs.utc(pass.EndTime);
            
            return {
              startTime: passStartUTC.format('YYYY/MM/DD HH:mm:ss'),
              endTime: passEndUTC.format('YYYY/MM/DD HH:mm:ss'),
              maxElevation: `${Math.floor(pass.MaxElevation)}°`,
              duration: `${Math.floor(pass.AvailableTime)} sec`,
              isDisabled: false
            };
          });
        
        allSchedules.push(...spacecraftSchedules);
      } catch (error) {
        console.error(`${satellite.name} 위성 스케줄 계산 실패:`, error);
      }
      
      // 겹치는 스케줄 확인
      const startDateAPI = dayjs.utc(todayStrUTC).format("YYYY-MM-DDTHH:mm:ss");
      const endDateAPI = dayjs.utc(todayStrUTC).add(1, 'day').format("YYYY-MM-DDTHH:mm:ss");
            
      let existingSchedules: any[] = [];
      try {
        const params = {
          start: startDateAPI,
          end: endDateAPI,
          gs: (selectedStation as any).gsId || (selectedStation as any).id,
        };
        const response = await axios.get('/api/schedule/check', { params });
        existingSchedules.push(...response.data);
      } catch (error) {
        console.error('기존 스케줄 가져오기 실패:', error);
      }
      
      // 겹치는 스케줄 확인
      const schedulesWithOverlap = allSchedules.map(schedule => {
        const isOverlapping = existingSchedules.some(existing => {
          const existingStart = dayjs(existing.startTime, "YYYY/MM/DD HH:mm:ss");
          const existingEnd = dayjs(existing.endTime, "YYYY/MM/DD HH:mm:ss");
          const newStart = dayjs(schedule.startTime, "YYYY/MM/DD HH:mm:ss");
          const newEnd = dayjs(schedule.endTime, "YYYY/MM/DD HH:mm:ss");
          
          const adjustedNewStart = newStart.subtract(bufferMinutes, 'minute');
          const adjustedNewEnd = newEnd.add(bufferMinutes, 'minute');
          
          return adjustedNewStart.isBefore(existingEnd) && adjustedNewEnd.isAfter(existingStart);
        });
        
        return {
          ...schedule,
          isDisabled: isOverlapping
        };
      });
      
      setScheduleData(schedulesWithOverlap);
    } catch (error) {
      console.error('스케줄 데이터 가져오기 실패:', error);
      setScheduleData([]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    scheduleData,
    isLoading,
    fetchSatelliteSchedule
  };
};
